import { computed, ref, type Ref } from 'vue'
import {
  isInsideBand,
  parsePrice,
  parseTokens,
  priceError,
  quantityError,
  type SellListingRules,
} from '../utils/sellListingUtils'

// Draft listing for the sell drawer: typed quantity + price, their instant
// checks against the server's rules (sell-overview) and whether the price
// sits in the band. Errors show once the field is blurred or Continue is
// pressed, then track the value live; an out-of-band price shows at once
// (Figma 34433:273271).

export interface SellListingSeed {
  tokens: number
  pricePerToken: number
}

export interface SellMarketFigures extends SellListingRules {
  valuation: number
}

export function useSellListingDraft(figures: Ref<SellMarketFigures>) {
  const tokensText = ref('')
  const priceText = ref('')
  const isQuantityChecked = ref(false)
  const isPriceChecked = ref(false)

  const tokens = computed(() => parseTokens(tokensText.value))
  const price = computed(() => parsePrice(priceText.value))

  const isOutsideBand = computed(
    () =>
      price.value !== null &&
      !isInsideBand(price.value, figures.value.bandMin, figures.value.bandMax),
  )

  const currentQuantityError = computed(() => quantityError(tokens.value, figures.value))
  const currentPriceError = computed(() => priceError(price.value, tokens.value, figures.value))

  const visibleQuantityError = computed(() =>
    isQuantityChecked.value ? currentQuantityError.value : null,
  )
  const visiblePriceError = computed(() =>
    isPriceChecked.value || isOutsideBand.value ? currentPriceError.value : null,
  )

  /** Shows that field's error (if any); true when the value can be listed. */
  const check = (field: 'quantity' | 'price'): boolean => {
    if (field === 'quantity') {
      isQuantityChecked.value = true
      return currentQuantityError.value === null
    }
    isPriceChecked.value = true
    return currentPriceError.value === null
  }

  /** Starts the price at the latest valuation the first time step 2 opens. */
  const suggestPrice = () => {
    if (!priceText.value && figures.value.valuation > 0) {
      priceText.value = figures.value.valuation.toFixed(2)
    }
  }

  /** Clears the draft — or seeds it from a prior listing ("Relist"). */
  const reset = (seed?: SellListingSeed | null) => {
    tokensText.value = seed && seed.tokens > 0 ? String(seed.tokens) : ''
    priceText.value = seed && seed.pricePerToken > 0 ? seed.pricePerToken.toFixed(2) : ''
    isQuantityChecked.value = false
    isPriceChecked.value = false
  }

  return {
    tokensText,
    priceText,
    tokens,
    price,
    isOutsideBand,
    errors: computed(() => ({
      quantity: visibleQuantityError.value,
      price: visiblePriceError.value,
    })),
    check,
    suggestPrice,
    reset,
  }
}
